"use client";

import { Xmark } from "iconoir-react";
import * as React from "react";

import { Link } from "@/components/ui/link";
import { cn } from "@/lib/utils";

interface AnnouncementBarProps {
  text: string;
  ctaLabel?: string;
  ctaHref?: string;
  ariaLabel?: string;
  className?: string;
}

function syncAnnouncementHeight(heightPx: number) {
  document.documentElement.style.setProperty(
    "--announcement-h",
    `${Math.round(heightPx)}px`
  );
  document.documentElement.dispatchEvent(
    new CustomEvent("announcement-h-change", {
      detail: { height: Math.round(heightPx) },
    })
  );
}

const MARQUEE_MASK = cn(
  "[mask-image:linear-gradient(to_right,transparent_0,black_20px,black_calc(100%-20px),transparent_100%)]",
  "[-webkit-mask-image:linear-gradient(to_right,transparent_0,black_20px,black_calc(100%-20px),transparent_100%)]"
);

function AnnouncementCopy({
  text,
  ctaLabel,
}: {
  text: string;
  ctaLabel?: string;
}) {
  if (!ctaLabel) return <>{text}</>;
  return (
    <>
      {text} <span className="underline underline-offset-2">{ctaLabel}</span>
    </>
  );
}

interface AnnouncementMarqueeProps {
  text: string;
  ctaLabel?: string;
  ctaHref?: string;
  paused: boolean;
  animate: boolean;
  onPauseChange: (paused: boolean) => void;
}

function AnnouncementMarquee({
  text,
  ctaLabel,
  ctaHref,
  paused,
  animate,
  onPauseChange,
}: AnnouncementMarqueeProps) {
  const trackClassName = cn(
    "flex w-max whitespace-nowrap",
    animate && "motion-safe:animate-[values-marquee_22s_linear_infinite]",
    paused && "[animation-play-state:paused]",
    !animate &&
      "w-full justify-center motion-reduce:w-full motion-reduce:justify-center",
    animate &&
      "motion-reduce:w-full motion-reduce:animate-none motion-reduce:justify-center"
  );

  const content = (
    <>
      <span className="pr-8">
        <AnnouncementCopy text={text} ctaLabel={ctaLabel} />
      </span>
      <span aria-hidden className="pr-8">
        <AnnouncementCopy text={text} ctaLabel={ctaLabel} />
      </span>
    </>
  );

  const sharedProps = {
    className: cn(
      "text-ds-body-sm block w-full min-w-0 overflow-hidden leading-snug text-white",
      MARQUEE_MASK,
      ctaHref && "transition-opacity hover:opacity-90 active:opacity-90"
    ),
    onTouchStart: () => onPauseChange(true),
    onTouchEnd: () => onPauseChange(false),
    onTouchCancel: () => onPauseChange(false),
    onMouseEnter: () => onPauseChange(true),
    onMouseLeave: () => onPauseChange(false),
  };

  if (ctaHref) {
    return (
      <Link
        href={ctaHref}
        {...sharedProps}
        data-ph-capture-attribute-cta_label="Announcement bar"
        data-ph-capture-attribute-cta_url={ctaHref}
      >
        <span className={trackClassName}>{content}</span>
      </Link>
    );
  }

  return (
    <div {...sharedProps}>
      <span className={trackClassName}>{content}</span>
    </div>
  );
}

interface AnnouncementStaticProps {
  text: string;
  ctaLabel?: string;
  ctaHref?: string;
}

function AnnouncementStatic({
  text,
  ctaLabel,
  ctaHref,
}: AnnouncementStaticProps) {
  return (
    <p className="text-ds-body-sm min-w-0 flex-1 text-center leading-snug whitespace-nowrap text-white md:flex-none md:leading-[1.4]">
      {text}
      {ctaLabel && ctaHref ? (
        <>
          {" "}
          <Link
            href={ctaHref}
            className="underline underline-offset-2 transition-opacity hover:opacity-80 motion-reduce:transition-none"
          >
            {ctaLabel}
          </Link>
        </>
      ) : null}
    </p>
  );
}

export function AnnouncementBar({
  text,
  ctaLabel,
  ctaHref,
  ariaLabel = "Site announcement",
  className,
}: AnnouncementBarProps) {
  const barRef = React.useRef<HTMLDivElement>(null);
  const contentAreaRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLSpanElement>(null);
  const [dismissed, setDismissed] = React.useState(false);
  const [closing, setClosing] = React.useState(false);
  const [marqueePaused, setMarqueePaused] = React.useState(false);
  // `null` until measured — render single-line marquee shell so copy never
  // wraps to two lines before overflow is known (avoids mobile layout shift).
  const [needsMarquee, setNeedsMarquee] = React.useState<boolean | null>(null);

  const updateMarqueeNeed = React.useCallback(() => {
    const measure = measureRef.current;
    const area = contentAreaRef.current;
    if (!measure || !area) return;

    setNeedsMarquee(measure.scrollWidth > area.clientWidth);
  }, []);

  React.useLayoutEffect(() => {
    updateMarqueeNeed();
  }, [updateMarqueeNeed, text, ctaLabel, dismissed]);

  React.useEffect(() => {
    const bar = barRef.current;
    if (!bar || dismissed) {
      syncAnnouncementHeight(0);
      return;
    }

    const measure = () => {
      updateMarqueeNeed();
      syncAnnouncementHeight(bar.getBoundingClientRect().height);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    if (contentAreaRef.current) observer.observe(contentAreaRef.current);
    window.addEventListener("resize", measure);

    const fonts = document.fonts;
    fonts?.addEventListener("loadingdone", measure);
    fonts?.ready.then(measure).catch(() => {});

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      fonts?.removeEventListener("loadingdone", measure);
    };
  }, [dismissed, text, ctaLabel, ctaHref, updateMarqueeNeed]);

  React.useEffect(() => {
    return () => {
      syncAnnouncementHeight(0);
    };
  }, []);

  const handleDismiss = () => {
    setClosing(true);
    syncAnnouncementHeight(0);
    window.setTimeout(() => setDismissed(true), 180);
  };

  if (dismissed) return null;

  return (
    <div
      ref={barRef}
      id="home-announcement-bar"
      role="region"
      aria-label={ariaLabel}
      className={cn(
        "fixed inset-x-0 top-0 z-[60] w-full",
        "bg-[linear-gradient(280deg,#FE634D_3%,#E641BD_40%,#6E2BF5_70%,#001EFF_100%)]",
        "transition-[height,min-height,padding,opacity] duration-180 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none",
        closing && "pointer-events-none min-h-0 overflow-hidden py-0 opacity-0",
        className
      )}
    >
      {/*
       * Marquee when copy exceeds one line at the current width; otherwise static
       * centered copy with an inline CTA link. Bar height is measured into
       * --announcement-h for nav/chapter offsets.
       */}
      <div className="relative flex w-full items-center gap-2 px-4 py-2 md:justify-center md:px-[88px]">
        <div ref={contentAreaRef} className="relative min-w-0 flex-1">
          <span
            ref={measureRef}
            aria-hidden
            className="text-ds-body-sm pointer-events-none invisible absolute top-0 left-0 whitespace-nowrap"
          >
            <AnnouncementCopy text={text} ctaLabel={ctaLabel} />
          </span>

          {needsMarquee === false ? (
            <AnnouncementStatic
              text={text}
              ctaLabel={ctaLabel}
              ctaHref={ctaHref}
            />
          ) : (
            <AnnouncementMarquee
              text={text}
              ctaLabel={ctaLabel}
              ctaHref={ctaHref}
              paused={marqueePaused}
              animate={needsMarquee === true}
              onPauseChange={setMarqueePaused}
            />
          )}
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss announcement"
          className={cn(
            "relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full",
            "bg-white/20 text-white transition-colors hover:bg-white/30",
            "md:absolute md:top-1/2 md:right-4 md:-translate-y-1/2"
          )}
        >
          <Xmark className="size-3" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
